import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Table,
  Panel,
  Row,
  Col,
  Message,
  Loader,
  Form,
  ButtonGroup,
  Tag,
  Divider,
  IconButton,
  Tooltip,
  Whisper,
  Placeholder,
  Modal,
  SelectPicker,
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ReloadIcon from '@rsuite/icons/Reload';
import UserInfoIcon from '@rsuite/icons/UserInfo';
import AdminDashboard from '../../components/AdminDashboard';
import UserService, { UserSearchParams } from '../../service/UserService';
import { User } from '../../my-type';
import { authUserData } from '../../service';
import { approveLeadApply, disableDriver, getDriver, getDriverPhoto } from '../../service/DriverService';
import { DriverStatus, TeamStatus } from '../../my-enum';
import { getTeamAdminUserUID, isTeamAdminUser } from '../../util/teamAdmin';

const { Column, HeaderCell, Cell } = Table;

const ROLE_OPTIONS = [
  { label: '司機', value: 'Driver' },
  { label: '管理員', value: 'Lead' },
];

const hasDriverRole = (roleString: string) => (
  roleString.split(',').some(role => role.trim().toLowerCase() === 'driver')
);

const getFleetRoles = (roleString: string) => (
  roleString.split(',').filter(role => role.trim().toLowerCase() !== 'user')
);

const UserList: React.FC = () => {
  const navigate = useNavigate();
  // Get authenticated user data
  const authUser = authUserData();

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState<UserSearchParams>({});
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [driverPhotoUrls, setDriverPhotoUrls] = useState<{
    licensePhotoUrl: string | null;
    motorLicensePhotoUrl: string | null;
    insurancePhotoUrl: string | null;
  }>({
    licensePhotoUrl: null,
    motorLicensePhotoUrl: null,
    insurancePhotoUrl: null,
  });
  const [roleUpdatingUserId, setRoleUpdatingUserId] = useState<number | null>(null);
  const [leadApplicationApprovingUserId, setLeadApplicationApprovingUserId] = useState<number | null>(null);
  const [disablingDriver, setDisablingDriver] = useState(false);

  // Form state for search inputs (removed teamCode)
  const [searchForm, setSearchForm] = useState({
    email: '',
    displayName: '',
    userName: '',
  });

  const enrichUsersWithDriverInfo = useCallback(async (searchResults: User[]) => {
    const usersWithDriverInfo = await Promise.all(
      searchResults.map(async user => {
        if (!hasDriverRole(user.roleString)) {
          return user;
        }

        try {
          const driver = await getDriver(user.id);
          return { ...user, driver };
        } catch (err) {
          console.error(`Error fetching driver for user ${user.id}:`, err);
          return user;
        }
      })
    );

    return usersWithDriverInfo;
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (params: UserSearchParams, role?: string | null) => {
    if (Object.values(params).every(value => !value)) {
      setUsers([]);
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const results = await UserService.searchUsers(params);
      const normalizedRole = role?.toLowerCase();
      const filteredResults = results.filter(user =>
        hasDriverRole(user.roleString) &&
        (!normalizedRole || user.roleString.split(',')
          .some(roleItem => roleItem.trim().toLowerCase() === normalizedRole))
      );
      const enrichedResults = await enrichUsersWithDriverInfo(filteredResults);
      setUsers(enrichedResults);

      if (enrichedResults.length === 0) {
        setError('沒有找到符合條件的用戶');
      } else {
        setSuccessMessage(`找到 ${enrichedResults.length} 位用戶`);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : '搜尋時發生錯誤');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [enrichUsersWithDriverInfo]);

  // Handle search form submission
  const handleSearch = () => {
    const params: UserSearchParams = {};

    // Always include team code from authenticated user
    const teamCode = authUser?.team?.code;
    if (teamCode) {
      params.teamCode = teamCode;
    } else {
      setError('無法獲取用戶團隊資訊');
      return;
    }

    if (searchForm.email.trim()) {
      const email = searchForm.email.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        params.email = email;
      } else {
        setError('請輸入有效的電子郵件格式');
        return;
      }
    }

    if (searchForm.displayName.trim()) {
      params.displayName = searchForm.displayName.trim();
    }

    if (searchForm.userName.trim()) {
      params.userName = searchForm.userName.trim();
    }

    // Search with just teamCode to show all drivers in the team
    // No additional validation needed as teamCode alone is sufficient

    setSearchParams(params);
    performSearch(params, roleFilter);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchForm({
      email: '',
      displayName: '',
      userName: '',
    });
    setSearchParams({});
    setUsers([]);
    setError('');
    setSuccessMessage('');
    setRoleFilter(null);
  };

  // Handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Show user details modal
  const showUserDetails = async (user: User) => {
    //get drive instance here by DriverService getDriverByUserId and assign to user.driver
    await getDriver(user.id)
      .then(driver => {
        user.driver = driver;
        setSelectedUser(user);
        setShowUserDetail(true);
      })
      .catch(err => {
        console.error('Error fetching driver:', err);
      });
  };

  const hasRole = (roleString: string, role: string) => (
    roleString
      .split(',')
      .some(roleItem => roleItem.trim().toLowerCase() === role.toLowerCase())
  );

  const teamAdminUserUID = getTeamAdminUserUID(authUser?.team);
  const isTeamLeader = isTeamAdminUser(authUser);

  const canManageLeadRole = (user: User) => (
    !!isTeamLeader &&
    user.id !== teamAdminUserUID
  );

  const handleLeadRoleUpdate = async (user: User, isLead: boolean) => {
    setRoleUpdatingUserId(user.id);
    setError('');
    setSuccessMessage('');

    try {
      const updatedUser = await UserService.updateUserLeadRole({
        targetUserUID: user.id,
        isLead,
      });

      setUsers(prevUsers =>
        prevUsers.map(existingUser => existingUser.id === updatedUser.id ? updatedUser : existingUser)
      );
      setSelectedUser(prevUser => prevUser?.id === updatedUser.id ? updatedUser : prevUser);
      setSuccessMessage(
        isLead
          ? `${updatedUser.displayName} 已設為代理隊長`
          : `${updatedUser.displayName} 的代理隊長權限已移除`
      );
    } catch (err) {
      console.error('Lead role update error:', err);
      setError(err instanceof Error ? err.message : '更新代理隊長權限時發生錯誤');
    } finally {
      setRoleUpdatingUserId(null);
    }
  };

  const handleApproveLeadApplication = async (user: User) => {
    setLeadApplicationApprovingUserId(user.id);
    setError('');
    setSuccessMessage('');

    try {
      const success = await approveLeadApply(user.id);

      if (!success) {
        throw new Error('核准代理隊長申請失敗');
      }

      const updatedUser = await UserService.getUserById(user.id);
      const updatedDriver = await getDriver(user.id);
      const mergedUser = {
        ...(updatedUser || user),
        driver: updatedDriver || user.driver,
      };

      setUsers(prevUsers =>
        prevUsers.map(existingUser => existingUser.id === user.id ? mergedUser : existingUser)
      );
      setSelectedUser(prevUser => prevUser?.id === user.id ? mergedUser : prevUser);
      setSuccessMessage(`${mergedUser.displayName} 的代理隊長申請已核准`);
    } catch (err) {
      console.error('Lead application approval error:', err);
      setError(err instanceof Error ? err.message : '核准代理隊長申請時發生錯誤');
    } finally {
      setLeadApplicationApprovingUserId(null);
    }
  };

  const handleDisableDriver = async () => {
    if (!selectedUser?.driver || disablingDriver) {
      return;
    }

    setDisablingDriver(true);
    setError('');
    setSuccessMessage('');

    try {
      const success = await disableDriver(selectedUser.id);

      if (!success) {
        throw new Error('停用司機失敗');
      }

      const updatedDriver = {
        ...selectedUser.driver,
        status: DriverStatus.停用中,
      };

      setSelectedUser(prev => (prev ? { ...prev, driver: updatedDriver } : prev));
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === selectedUser.id ? { ...user, driver: updatedDriver } : user
        )
      );
      setSuccessMessage(`${selectedUser.displayName} 已停用`);
    } catch (err) {
      console.error('Disable driver error:', err);
      setError(err instanceof Error ? err.message : '停用司機時發生錯誤');
    } finally {
      setDisablingDriver(false);
    }
  };

  const driverPhotoStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 240,
    maxHeight: 160,
    objectFit: 'contain',
    borderRadius: 4,
    border: '1px solid #e5e5e5',
  };

  const updateDriverPhotoUrls = useCallback(
    (next: Partial<{
      licensePhotoUrl: string | null;
      motorLicensePhotoUrl: string | null;
      insurancePhotoUrl: string | null;
    }>) => {
      setDriverPhotoUrls(prev => {
        const updated = { ...prev, ...next };
        if (prev.licensePhotoUrl && prev.licensePhotoUrl !== updated.licensePhotoUrl) {
          URL.revokeObjectURL(prev.licensePhotoUrl);
        }
        if (
          prev.motorLicensePhotoUrl &&
          prev.motorLicensePhotoUrl !== updated.motorLicensePhotoUrl
        ) {
          URL.revokeObjectURL(prev.motorLicensePhotoUrl);
        }
        if (prev.insurancePhotoUrl && prev.insurancePhotoUrl !== updated.insurancePhotoUrl) {
          URL.revokeObjectURL(prev.insurancePhotoUrl);
        }
        return updated;
      });
    },
    []
  );

  useEffect(() => {
    if (!showUserDetail || !selectedUser?.driver) {
      updateDriverPhotoUrls({
        licensePhotoUrl: null,
        motorLicensePhotoUrl: null,
        insurancePhotoUrl: null,
      });
      return;
    }

    let isActive = true;
    const fetchPhotos = async () => {
      const [licensePhotoUrl, motorLicensePhotoUrl, insurancePhotoUrl] = await Promise.all([
        selectedUser.driver?.carLicensePhoto
          ? getDriverPhoto(selectedUser.driver.carLicensePhoto)
          : Promise.resolve(null),
        selectedUser.driver?.motorLicensePhoto
          ? getDriverPhoto(selectedUser.driver.motorLicensePhoto)
          : Promise.resolve(null),
        selectedUser.driver?.insurancePhoto
          ? getDriverPhoto(selectedUser.driver.insurancePhoto)
          : Promise.resolve(null),
      ]);

      if (!isActive) {
        if (licensePhotoUrl) {
          URL.revokeObjectURL(licensePhotoUrl);
        }
        if (motorLicensePhotoUrl) {
          URL.revokeObjectURL(motorLicensePhotoUrl);
        }
        if (insurancePhotoUrl) {
          URL.revokeObjectURL(insurancePhotoUrl);
        }
        return;
      }

      updateDriverPhotoUrls({ licensePhotoUrl, motorLicensePhotoUrl, insurancePhotoUrl });
    };

    fetchPhotos();

    return () => {
      isActive = false;
    };
  }, [selectedUser, showUserDetail, updateDriverPhotoUrls]);

  // Format date for display
  const formatDate = (date: Date | null | string) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('zh-TW');
    } catch {
      return 'Invalid Date';
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string): "red" | "orange" | "yellow" | "green" | "cyan" | "blue" | "violet" => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'red';
      case 'driver':
        return 'blue';
      case 'lead':
        return 'orange';
      default:
        return 'cyan';
    }
  };

  const getRoleLabel = (role: string) => {
    const match = ROLE_OPTIONS.find(option => option.value.toLowerCase() === role.toLowerCase());
    return match ? match.label : role;
  };

  return (
    <AdminDashboard title="用戶管理">
      <Panel header="搜尋團隊司機" bordered className="fleet-filter-panel">
        <p>僅查詢及維護團隊司機資料。</p>
        <Form layout="vertical" className="fleet-directory-filter">
          <Row gutter={16}>
            <Col md={8}>
              <Form.Group>
                <Form.ControlLabel htmlFor="search-email">電子郵件</Form.ControlLabel>
                <Input id="search-email"
                  placeholder="輸入電子郵件"
                  value={searchForm.email}
                  onChange={(value) => setSearchForm(prev => ({ ...prev, email: value }))}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group>
                <Form.ControlLabel htmlFor="search-displayName">顯示名稱</Form.ControlLabel>
                <Input id="search-displayName"
                  placeholder="輸入顯示名稱"
                  value={searchForm.displayName}
                  onChange={(value) => setSearchForm(prev => ({ ...prev, displayName: value }))}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col md={8}>
              <Form.Group>
                <Form.ControlLabel htmlFor="search-userName">用戶帳號</Form.ControlLabel>
                <Input id="search-userName"
                  placeholder="輸入用戶帳號"
                  value={searchForm.userName}
                  onChange={(value) => setSearchForm(prev => ({ ...prev, userName: value }))}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group>
                <Form.ControlLabel id="search-role-label">角色</Form.ControlLabel>
                <SelectPicker aria-labelledby="search-role-label"
                  data={ROLE_OPTIONS}
                  value={roleFilter}
                  onChange={(value) => setRoleFilter(value || null)}
                  cleanable
                  block
                  placeholder="選擇角色"
                  searchable={false}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Display current team code */}
          {/* <Row style={{ marginTop: '10px' }}>
            <Col>
              <Message type="info" style={{ margin: '0' }}>
                <strong>搜尋範圍：</strong>團隊 {authUser?.team?.name || 'N/A'} ({authUser?.team?.code || 'N/A'})
              </Message>
            </Col>
          </Row> */}

          <Row style={{ marginTop: '20px' }}>
            <Col>
              <ButtonGroup>
                <Button
                  appearance="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? '搜尋中...' : '搜尋'}
                </Button>
                <Button
                  appearance="ghost"
                  startIcon={<UserInfoIcon />}
                  onClick={() => {
                    // Clear form and search all drivers in the team
                    setSearchForm({ email: '', displayName: '', userName: '' });
                    setRoleFilter(null);
                    const teamCode = authUser?.team?.code;
                    if (teamCode) {
                      const params: UserSearchParams = { teamCode };
                      setSearchParams(params);
                      performSearch(params, null);
                    }
                  }}
                  loading={loading}
                  disabled={loading}
                >
                  顯示全部團隊司機
                </Button>
                <Button
                  appearance="subtle"
                  startIcon={<ReloadIcon />}
                  onClick={handleClearSearch}
                >
                  清除
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Form>
      </Panel>

      {error && (
        <Message type="error" style={{ margin: '20px 0' }}>
          {error}
        </Message>
      )}

      {successMessage && (
        <Message type="success" style={{ margin: '20px 0' }}>
          {successMessage}
        </Message>
      )}

      <Panel header={`搜尋結果 (${users.length} 筆)`} bordered style={{ marginTop: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader size="lg" content="搜尋中..." />
          </div>
        ) : users.length > 0 ? (
          <Table
            data={users}
            autoHeight
            hover
            bordered
            cellBordered
            rowHeight={60}
          >
            <Column width={160} fixed>
              <HeaderCell>用戶帳號</HeaderCell>
              <Cell>
                {(rowData: User) => (rowData.authProvider === 'LINE' ? '' : rowData.userId)}
              </Cell>
            </Column>

            <Column width={150}>
              <HeaderCell>顯示名稱</HeaderCell>
              <Cell dataKey="displayName" />
            </Column>

            <Column width={150}>
              <HeaderCell>司機名稱</HeaderCell>
              <Cell>
                {(rowData: User) => (
                  hasDriverRole(rowData.roleString) ? rowData.driver?.driverName || '-' : '-'
                )}
              </Cell>
            </Column>

            <Column width={200}>
              <HeaderCell>電子郵件</HeaderCell>
              <Cell dataKey="email" />
            </Column>

            <Column width={120}>
              <HeaderCell>登入方式</HeaderCell>
              <Cell>
                {(rowData: User) => rowData.authProvider || 'N/A'}
              </Cell>
            </Column>

            <Column width={200} align="left">
              <HeaderCell>角色</HeaderCell>
              <Cell>
                {(rowData: User) => (
                  <>
                    {getFleetRoles(rowData.roleString)
                      .map(role => (
                        <Tag
                          key={role.trim()}
                          color={getRoleBadgeColor(role.trim())}
                          style={{ marginRight: 4 }}
                        >
                          {getRoleLabel(role.trim())}
                        </Tag>
                      ))}
                  </>
                )}
              </Cell>
            </Column>

            <Column width={150}>
              <HeaderCell>代理申請</HeaderCell>
              <Cell>
                {(rowData: User) => {
                  if (rowData.driver?.leadApplicationStatus === 0) {
                    return <Tag color="orange">待審核</Tag>;
                  }
                  if (rowData.driver?.leadApplicationStatus === 1) {
                    return <Tag color="green">已核准</Tag>;
                  }
                  return '-';
                }}
              </Cell>
            </Column>

            <Column width={150}>
              <HeaderCell>最後登入</HeaderCell>
              <Cell>
                {(rowData: User) => formatDate(rowData.lastLogin)}
              </Cell>
            </Column>

            <Column width={220} fixed="right">
              <HeaderCell>操作</HeaderCell>
              <Cell>
                {(rowData: User) => {
                  const rowHasLeadRole = hasRole(rowData.roleString, 'Lead');
                  const hasPendingLeadApplication = rowData.driver?.leadApplicationStatus === 0;

                  return (
                    <ButtonGroup>
                      <Whisper
                        placement="top"
                        speaker={<Tooltip>查看詳細資訊</Tooltip>}
                      >
                        <IconButton
                          size="sm"
                          icon={<UserInfoIcon />}
                          onClick={() => showUserDetails(rowData)}
                        />
                      </Whisper>
                      {canManageLeadRole(rowData) && (
                        <Whisper
                          placement="top"
                          speaker={<Tooltip>{rowHasLeadRole ? '移除代理隊長權限' : '設為代理隊長'}</Tooltip>}
                        >
                          <Button
                            size="sm"
                            appearance={rowHasLeadRole ? 'ghost' : 'primary'}
                            color={rowHasLeadRole ? 'red' : 'orange'}
                            loading={roleUpdatingUserId === rowData.id}
                            disabled={roleUpdatingUserId !== null}
                            onClick={() => handleLeadRoleUpdate(rowData, !rowHasLeadRole)}
                          >
                            {rowHasLeadRole ? '移除代理' : '設為代理'}
                          </Button>
                        </Whisper>
                      )}
                      {canManageLeadRole(rowData) && hasPendingLeadApplication && !rowHasLeadRole && (
                        <Whisper
                          placement="top"
                          speaker={<Tooltip>核准代理隊長申請</Tooltip>}
                        >
                          <Button
                            size="sm"
                            appearance="primary"
                            color="green"
                            loading={leadApplicationApprovingUserId === rowData.id}
                            disabled={leadApplicationApprovingUserId !== null}
                            onClick={() => handleApproveLeadApplication(rowData)}
                          >
                            核准申請
                          </Button>
                        </Whisper>
                      )}
                    </ButtonGroup>
                  );
                }}
              </Cell>
            </Column>
          </Table>
        ) : !loading && Object.values(searchParams).some(v => v) ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Placeholder.Paragraph rows={3} />
            <p style={{ color: '#999' }}>沒有找到符合條件的用戶</p>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Placeholder.Paragraph rows={3} />
            <p style={{ color: '#999' }}>請輸入搜尋條件來查找團隊司機，或點擊「顯示全部團隊司機」查看所有司機</p>
          </div>
        )}
      </Panel>

      {/* User Detail Modal */}
      <Modal
        open={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setDisablingDriver(false);
        }}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>用戶詳細資訊</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <Row gutter={16}>
                <Col md={12}>
                  <h6>基本資訊</h6>
                  <Divider />
                  <p><strong>ID:</strong> {selectedUser.id}</p>
                  <p><strong>用戶ID:</strong> {selectedUser.userId}</p>
                  <p><strong>顯示名稱:</strong> {selectedUser.displayName}</p>
                  <p><strong>電子郵件:</strong> {selectedUser.email}</p>
                  <p><strong>角色:</strong>
                    <span style={{ marginLeft: '8px' }}>
                      {getFleetRoles(selectedUser.roleString).map(role => (
                        <Tag
                          key={role.trim()}
                          color={getRoleBadgeColor(role.trim())}
                          style={{ marginRight: 4 }}
                        >
                          {getRoleLabel(role.trim())}
                        </Tag>
                      ))}
                    </span>
                  </p>
                  {hasRole(selectedUser.roleString, 'Lead') && selectedUser.id !== teamAdminUserUID && (
                    <p><strong>身份說明:</strong> 團隊代理隊長</p>
                  )}
                </Col>

                <Col md={12}>
                  <h6>團隊資訊</h6>
                  <Divider />
                  {authUser?.team ? (
                    <>
                      <p><strong>團隊名稱:</strong> {authUser?.team.name}</p>
                      <p><strong>團隊代碼:</strong> {authUser?.team.code}</p>
                      <p><strong>管理員ID:</strong> {teamAdminUserUID ?? '-'}</p>
                      <p><strong>團隊狀態:</strong> {TeamStatus[authUser?.team.status]}</p>
                      {authUser?.team.phone && (
                        <p><strong>電話:</strong> {authUser?.team.phone}</p>
                      )}
                      {authUser?.team.email && (
                        <p><strong>團隊信箱:</strong> {authUser?.team.email}</p>
                      )}
                    </>
                  ) : (
                    <p>無團隊資訊</p>
                  )}
                </Col>
              </Row>

              {selectedUser.driver && (
                <>
                  <Divider />
                  <h6>司機資訊</h6>
                  <Divider />
                  <Row gutter={16}>
                    <Col md={12}>
                      <p><strong>司機姓名:</strong> {selectedUser.driver.driverName}</p>
                      <p><strong>身分證號:</strong> {selectedUser.driver.idCardNo}</p>
                      <p><strong>銀行代碼:</strong> {selectedUser.driver.bankID || '未提供'}</p>
                      <p><strong>銀行帳號:</strong> {selectedUser.driver.bankAccount || '未提供'}</p>
                      <p><strong>電話:</strong> {selectedUser.driver.phoneNumber}</p>
                      <p><strong>地址:</strong> {selectedUser.driver.address}</p>
                      <p><strong>車牌號碼:</strong> {selectedUser.driver.licencePlateNumber}</p>
                    </Col>
                    <Col md={12}>
                      <p><strong>車輛品牌:</strong> {selectedUser.driver.carBrand}</p>
                      <p><strong>車輛型號:</strong> {selectedUser.driver.carModel}</p>
                      <p><strong>車輛顏色:</strong> {selectedUser.driver.carColor}</p>
                      <p><strong>當前里程:</strong> {selectedUser.driver.currentMileage} km</p>
                      <p><strong>司機狀態:</strong>
                        <Tag color="blue" style={{ marginLeft: '8px' }}>
                          {DriverStatus[selectedUser.driver.status]}
                        </Tag>
                      </p>
                      <p><strong>代理申請:</strong>
                        <Tag
                          color={
                            selectedUser.driver.leadApplicationStatus === 0
                              ? 'orange'
                              : selectedUser.driver.leadApplicationStatus === 1
                                ? 'green'
                                : 'blue'
                          }
                          style={{ marginLeft: '8px' }}
                        >
                          {selectedUser.driver.leadApplicationStatus === 0
                            ? '待審核'
                            : selectedUser.driver.leadApplicationStatus === 1
                              ? '已核准'
                              : '未申請'}
                        </Tag>
                      </p>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col md={8}>
                      <p><strong>駕照照片:</strong></p>
                      {driverPhotoUrls.licensePhotoUrl ? (
                        <img
                          src={driverPhotoUrls.licensePhotoUrl}
                          alt="駕照照片"
                          style={driverPhotoStyle}
                        />
                      ) : (
                        <p style={{ color: '#999' }}>未提供</p>
                      )}
                    </Col>
                    <Col md={8}>
                      <p><strong>行照照片:</strong></p>
                      {driverPhotoUrls.motorLicensePhotoUrl ? (
                        <img
                          src={driverPhotoUrls.motorLicensePhotoUrl}
                          alt="行照照片"
                          style={driverPhotoStyle}
                        />
                      ) : (
                        <p style={{ color: '#999' }}>未提供</p>
                      )}
                    </Col>
                    <Col md={8}>
                      <p><strong>保險證:</strong></p>
                      {driverPhotoUrls.insurancePhotoUrl ? (
                        <img
                          src={driverPhotoUrls.insurancePhotoUrl}
                          alt="保險證"
                          style={driverPhotoStyle}
                        />
                      ) : (
                        <p style={{ color: '#999' }}>未提供</p>
                      )}
                    </Col>
                  </Row>
                </>
              )}

              <Divider />
              <h6>認證資訊</h6>
              <Divider />
              <p><strong>Token到期:</strong> {formatDate(selectedUser.tokenExpiry)}</p>
              <p><strong>LINE連結:</strong> {selectedUser.lineAccessToken ? '已連結' : '未連結'}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedUser && hasDriverRole(selectedUser.roleString) && (
            <>
              {canManageLeadRole(selectedUser) &&
                selectedUser.driver?.leadApplicationStatus === 0 &&
                !hasRole(selectedUser.roleString, 'Lead') && (
                  <Button
                    onClick={() => handleApproveLeadApplication(selectedUser)}
                    appearance="primary"
                    color="green"
                    loading={leadApplicationApprovingUserId === selectedUser.id}
                    disabled={leadApplicationApprovingUserId !== null}
                  >
                    核准代理申請
                  </Button>
                )}
              <Button
                onClick={handleDisableDriver}
                appearance="primary"
                color="red"
                loading={disablingDriver}
                disabled={selectedUser.driver?.status === DriverStatus.停用中}
              >
                {selectedUser.driver?.status === DriverStatus.停用中 ? '司機已停用' : '停用司機'}
              </Button>
              <Button
                onClick={() => {
                  if (selectedUser?.driver) {
                    setShowUserDetail(false);
                    navigate(`/drivers/${selectedUser.driver.userUID}/edit`);
                  }
                }}
                appearance="ghost"
                disabled={disablingDriver}
              >
                前往編輯司機
              </Button>
            </>
          )}
          <Button onClick={() => setShowUserDetail(false)} appearance="primary">
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminDashboard>
  );
};

export default UserList;
